import type { ReactNode, ChangeEvent } from 'react';
import { safeExternalUrl } from '../lib/format.js';

export function Spinner({ label = '불러오는 중…' }: { label?: string }) {
  return (
    <div className="state" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/**
 * 오류 상자.
 *
 * **오류가 없으면 아무것도 그리지 않는다.** 예전에는 error 를 그대로 String()
 * 했기 때문에, 아직 실패한 적 없는 mutation 의 `error`(= null)를 그냥 넘기는
 * 호출부에서 빨간 상자에 "오류 null" 이 상시 떠 있었다(배치관리 화면이 그랬다).
 * 대부분의 호출부는 `isError &&` 로 감싸서 이 문제를 피해 갔지만, 감싸는 것을
 * 잊어도 화면에 거짓 오류가 뜨지 않는 편이 낫다.
 */
export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error === null || error === undefined) return null;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : // Error 도 문자열도 아닌 값(객체 등)을 String() 하면 "[object Object]" 가
          // 그대로 보인다. 사용자에게는 아무 뜻도 없으므로 일반 문구로 바꾼다.
          '알 수 없는 오류가 발생했습니다.';
  return (
    <div className="state state--error" role="alert">
      <strong>오류</strong>
      <span>{message}</span>
      {onRetry && (
        <button type="button" className="btn btn--ghost" onClick={onRetry}>
          다시 시도
        </button>
      )}
    </div>
  );
}

export function EmptyBox({ children = '조건에 맞는 데이터가 없습니다.' }: { children?: ReactNode }) {
  return <div className="state">{children}</div>;
}

/**
 * 검색·필터 줄.
 *
 * 화면마다 `<div className="filters">` 를 손으로 열고, 초기화 버튼은 어떤
 * 화면에는 있고(주문 관리) 어떤 화면에는 없었다(상품 관리). 같은 자리에 같은
 * 버튼이 있도록 여기 하나로 모은다 - `onReset` 을 주면 오른쪽 끝에 붙는다.
 */
export function FilterBar({
  children,
  onReset,
}: {
  children: ReactNode;
  /** 주면 '필터 초기화' 버튼이 오른쪽 끝에 붙는다. */
  onReset?: () => void;
}) {
  return (
    <div className="filters">
      {children}
      {onReset && (
        <button type="button" className="btn btn--ghost filters__reset" onClick={onReset}>
          필터 초기화
        </button>
      )}
    </div>
  );
}

/**
 * 상태 한 칸.
 *
 * `.badge` 에 `--ok`/`--warn`/`--danger` 를 손으로 붙여 쓰던 것을 뜻으로
 * 부른다. 색을 고르는 판단이 화면마다 흩어지지 않게 한다.
 */
export function StatusBadge({
  label,
  tone = 'neutral',
  title,
}: {
  label: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'memo';
  title?: string;
}) {
  const cls = tone === 'neutral' ? 'badge' : `badge badge--${tone}`;
  return (
    <span className={cls} title={title}>
      {label}
    </span>
  );
}

/**
 * 썸네일 + 이름 한 칸. 상품·랜덤박스를 목록에서 보여주는 자리에 쓴다.
 *
 * 세 화면이 같은 `product-cell` 조합을 손으로 적고 있었고, `loading="lazy"` 가
 * 한 곳에서는 빠져 있었다.
 */
export function ProductCell({
  name,
  thumbnail,
  sub,
}: {
  name: ReactNode;
  thumbnail?: string | null;
  /** 이름 아래 작게 붙는 보조 정보(카테고리·코드 등). */
  sub?: ReactNode;
}) {
  return (
    <span className="product-cell">
      {thumbnail ? <img className="thumb" src={thumbnail} alt="" loading="lazy" /> : null}
      <span>
        {name}
        {sub ? <span className="td-sub">{sub}</span> : null}
      </span>
    </span>
  );
}

export function Field({
  label,
  children,
  full,
  as = 'label',
  error,
}: {
  label: string;
  children: ReactNode;
  /** grid 안에서 한 줄 전체를 차지해야 하는 칸(예: 메모). */
  full?: boolean;
  /**
   * 기본은 <label> — 라벨 텍스트를 눌러도 안의 <input>/<select>가 포커스된다.
   * 하지만 <label>은 안에 있는 유일한 라벨링 가능 엘리먼트(button 포함)로 클릭을
   * 그대로 한 번 더 forwarding 한다 — 커스텀 드롭다운처럼 여러 버튼을 감싸는
   * 필드는 이 forwarding 때문에 "옵션을 눌러도 방금 닫은 걸 트리거 버튼이 다시
   * 열어버리는" 오작동이 난다. 그런 필드는 as="div" 로 forwarding 자체를 없앤다.
   */
  as?: 'label' | 'div';
  /** 검증에 걸린 이유. 주면 칸 아래에 빨간 문구가 붙는다. */
  error?: string | undefined;
}) {
  const Tag = as;
  return (
    <Tag className={full ? 'field field--full' : 'field'}>
      <span className="field__label">{label}</span>
      {children}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : null}
    </Tag>
  );
}

export function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return <div className="pagination"><span className="pagination__info">총 {total.toLocaleString()}건</span></div>;
  }

  // 현재 페이지 주변만 보여준다. 페이지가 수천 개여도 버튼이 폭발하지 않게.
  const windowSize = 2;
  const start = Math.max(1, page - windowSize);
  const end = Math.min(totalPages, page + windowSize);
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <nav className="pagination" aria-label="페이지 이동">
      <span className="pagination__info">
        총 {total.toLocaleString()}건 · {page} / {totalPages} 페이지
      </span>
      <div className="pagination__buttons">
        <button type="button" className="btn btn--ghost" disabled={page <= 1} onClick={() => onChange(1)}>
          «
        </button>
        <button type="button" className="btn btn--ghost" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          이전
        </button>
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            className={p === page ? 'btn btn--page btn--active' : 'btn btn--page'}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          className="btn btn--ghost"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
        >
          다음
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={page >= totalPages}
          onClick={() => onChange(totalPages)}
        >
          »
        </button>
      </div>
    </nav>
  );
}

/** 정렬 가능한 테이블 헤더 셀. */
export function SortableTh({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
  align,
  width,
}: {
  label: string;
  sortKey: string;
  currentSort: string | undefined;
  currentDir: 'asc' | 'desc';
  onSort: (key: string, dir: 'asc' | 'desc') => void;
  align?: 'right' | 'center';
  width?: string;
}) {
  const active = currentSort === sortKey;
  const nextDir: 'asc' | 'desc' = active && currentDir === 'asc' ? 'desc' : 'asc';
  return (
    <th style={{ textAlign: align, width }} aria-sort={active ? (currentDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="th-sort" onClick={() => onSort(sortKey, nextDir)}>
        {label}
        <span className={active ? 'th-sort__arrow th-sort__arrow--on' : 'th-sort__arrow'}>
          {active ? (currentDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** 입력 후 일정 시간 멈출 때까지 기다렸다가 값을 올려보내는 검색 입력. */
export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      className="input"
      type="search"
      value={value}
      placeholder={placeholder}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  );
}

/**
 * 바깥으로 나가는 링크.
 *
 * `href` 가 http(s) 가 아니면 **링크를 만들지 않고** 내용만 그대로 그린다.
 * 경품 썸네일 주소처럼 관리자가 자유롭게 타이핑해 DB 에 들어간 값이 `<a href>`
 * 로 바로 들어가는 자리를 위한 것이다 - `javascript:` 를 적어 두면 그 링크를
 * 누른 다른 관리자의 브라우저에서 실행된다(safeExternalUrl 주석 참고).
 *
 * `rel="noreferrer"` 는 여기서 한 번만 정해 둔다. 자리마다 손으로 적으면
 * 언젠가 한 곳이 빠진다.
 */
export function SafeLink({
  href,
  title,
  className,
  children,
}: {
  href: unknown;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  const safe = safeExternalUrl(href);
  if (!safe) return <span className={className} title={title}>{children}</span>;
  return (
    <a className={className} href={safe} target="_blank" rel="noreferrer" title={title}>
      {children}
    </a>
  );
}
